import './styles.scss';

import {
  AcceptInputOption,
  ReportFilterEnum,
  ReportInput,
  ReportSeriesEnum,
  SortDirectionEnum,
  TripTableFormatEnum,
  TripTypeEnum,
} from '@/models/gen/graphql';
import { Dayts, DaytsFormatEnum, getResultFromState, handleError, onEnter, saveFile } from '@/utils';
import Input, { InputWithIcon } from '@/components/Input';
import { TODAY, TODAY_EOD } from '@/constants';
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import AirlineIataDropdown from '@/components/AirlineDropdown';
import { AirlineTripSortColumnEnum } from '@/features/AirlineTrips/components/TripsTable/hook';
import { AirportGroupDropdown } from '@/components/AirportDropdown';
import ClientDropdown from '@/components/ClientDropdown';
import { Collapse } from 'react-bootstrap';
import DateRangePicker from '@/components/DateRangePicker';
import FormButton from '@/components/FormButton';
import { GetCompletionReasonsDocument } from '@/api/queries';
import HasPermission from '@/components/HasPermission';
import LocationQueryDropdown from '@/components/LocationDropdown';
import QueryFilters from '@/components/QueryFilters';
import RateValuesDropdown from '@/components/RateValuesDropdown';
import React from 'react';
import { TripTypeDropdownMulti } from '@/components/TripTypeDropdown';
import TripsTableFormatDropdown from '@/components/TripsTableFormatDropdown';
import { createPersistentComponentState } from '@/state';
import { customSearchStorage } from '@/state/storage';
import { reportQueryAsCsv } from '@/api/services/reports/reportQuery';
import useDb from '@/hooks/useDb';
import useHotkeys from '@/hooks/useHotkeys';
import usePopState from '@/hooks/usePopState';

export type AirlineTripsFiltersState = {
  from: string;
  to: string;
  airportCode: string[];
  payerProviderId: string;
  doLocationId: string[];
  puLocationId: string[];
  flightNumber: string;
  type: string[];
  servicerIataAirlineCode: string;
  search: string;
  rateAmount: string;
  format: TripTableFormatEnum;
};
export const initAirlineTripsFiltersState: AirlineTripsFiltersState = {
  servicerIataAirlineCode: '',
  airportCode: [],
  payerProviderId: '',
  doLocationId: [],
  flightNumber: '',
  from: TODAY,
  puLocationId: [],
  to: TODAY_EOD,
  type: [],
  search: '',
  rateAmount: '',
  format: TripTableFormatEnum.All,
};

const useAirlineTripFilters = createPersistentComponentState('useAirlineTripFilters', initAirlineTripsFiltersState, {
  live: false,
  storage: customSearchStorage,
  reviver: (key: string, value: any) => {
    switch (key) {
      case 'type': {
        const tripTypeEnumKeys = Object.values(TripTypeEnum);
        return (Array.isArray(value) ? value : [value]).filter((key) => tripTypeEnumKeys.includes(key));
      }
      case 'format':
        return Object.values(TripTableFormatEnum).includes(value) ? value : initAirlineTripsFiltersState.format;
      default: {
        return value;
      }
    }
  },
});

type TripFiltersProps = {
  onCreate?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onSubmit: (filters: AirlineTripsFiltersState) => Promise<void>;
  onReset?: (filters: AirlineTripsFiltersState) => Promise<void>;
  onSearch?: (search: string) => void;
  sorting?: Array<{ column: AirlineTripSortColumnEnum; direction: SortDirectionEnum }>;
  loading?: boolean;
};
export type TripFiltersRefMethods = {
  quickFilter: (input: AirlineTripsFiltersState | ((current: AirlineTripsFiltersState) => AirlineTripsFiltersState)) => void;
};

const TripFilters = (
  { onSubmit, onReset, onSearch, sorting, loading }: TripFiltersProps,
  ref: React.ForwardedRef<TripFiltersRefMethods>
): React.JSX.Element => {
  // state
  const state = useAirlineTripFilters(({ state }) => state);
  const setState = useAirlineTripFilters(({ setState }) => setState);
  const saveState = useAirlineTripFilters(({ saveState }) => saveState);
  const rehydrateState = useAirlineTripFilters(({ rehydrate }) => rehydrate);

  const [lastQuery, setLastQuery] = useState(state);
  const [expanded, setExpanded] = useState(true);

  // Hotkeys
  useHotkeys('v4_trips_filters', {
    'shift+ctrl+s': {
      name: 'Focus Search Bar',
      description: 'Focuses the Search Bar',
      action: () => (document.querySelector('.TripFilters input[name="search"]') as HTMLElement)?.focus?.(),
    },
    'shift+ctrl+f': {
      name: 'Focus Flight Number',
      description: 'Focuses the Flight Number field',
      action: () => (document.querySelector('.TripFilters input[name="flightNumber"]') as HTMLElement)?.focus?.(),
    },
    'shift+enter': {
      name: 'Search Trips',
      description: 'Search Trips with the Current Filters',
      action: () => (document.querySelector('.TripFilters button[name="submitFilters"]') as HTMLElement)?.click?.(),
    },
    'shift+ctrl+r': {
      name: 'Reset Trips Search',
      description: 'Search Trips with the Default Filters',
      action: () => (document.querySelector('.TripFilters button[name="resetFilters"]') as HTMLElement)?.click?.(),
    },
  });

  const handleChange = useCallback(
    (name: string) =>
      (value: unknown): void =>
        setState((current) => ({ ...current, [name]: value })),
    [setState]
  );

  const handleSubmit = useCallback((): void => {
    // get the latest state from closure to ensure it's always accurate
    const { state } = useAirlineTripFilters.getState();
    saveState();
    setLastQuery(state);
    onSubmit(state);
  }, [state, saveState, onSubmit]);

  // This is exposed via ref so the parent can utilize shortcut functionality
  const handleQuickFilter = useCallback(
    (input: AirlineTripsFiltersState | ((current: AirlineTripsFiltersState) => AirlineTripsFiltersState)): void => {
      const result = getResultFromState(input, state);
      setState(result);
      setLastQuery(result);
      saveState();
      onSubmit(result);
    },
    [state, saveState, onSubmit, setState]
  );

  const handleReset = useCallback((): void => {
    const from = new Dayts().startOf('day').format(Dayts.Format.DATETIME_INPUT);
    const to = new Dayts().endOf('day').format(Dayts.Format.DATETIME_INPUT);
    setState({ ...initAirlineTripsFiltersState, from, to });
    setLastQuery({ ...initAirlineTripsFiltersState, from, to });
    saveState();
    onReset({ ...initAirlineTripsFiltersState, from, to });
  }, [setState, saveState, onReset]);

  useImperativeHandle(ref, () => ({ quickFilter: handleQuickFilter }), [handleQuickFilter]);

  const onExpand = (): void => setExpanded((current: boolean): boolean => !current);
  const handleSearch = (): void => onSearch(state.search);

  const handlePopState = useCallback(() => rehydrateState().then(handleSubmit), [rehydrateState, handleSubmit]);
  usePopState(handlePopState);

  useEffect((): void => {
    handlePopState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  const { data: getCompletionReasonsResponse } = useDb('CompletionReasons', GetCompletionReasonsDocument, {
    lazy: true,
    ttl: 360, // cache for 1 day
  });
  const completionReasons = useMemo(
    () => getCompletionReasonsResponse?.getFilters?.filters?.completionReasons || [],
    [getCompletionReasonsResponse]
  );

  const getCompletionTypes = useCallback(
    (format: TripTableFormatEnum): string[] => {
      const noShow = completionReasons.find((reason: AcceptInputOption): boolean => reason?.displayName === 'No Show')?.id;
      const taxiCalled = completionReasons
        ?.filter((reason: AcceptInputOption): boolean => ['Taxi SHG Paid', 'Taxi Crew Paid', 'Lyft'].includes(reason?.displayName))
        ?.map((reason: AcceptInputOption): string => reason?.id);
      switch (format) {
        case TripTableFormatEnum.NoShow:
          return noShow ? [noShow] : null;
        case TripTableFormatEnum.TaxiCalled:
          return taxiCalled;
        default:
          return null;
      }
    },
    [completionReasons]
  );

  const getCsv = useCallback(async (): Promise<void> => {
    try {
      const payload = formatReportInput(lastQuery, getCompletionTypes(lastQuery.format));
      const response = await reportQueryAsCsv('detailedReport')(payload);
      if (!response) return;
      const blob = new Blob([response], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      saveFile(url, `Trips-${new Dayts().formatAs(DaytsFormatEnum.DATE_INPUT)}.csv`);
    } catch (err) {
      handleError(err, { notification: { title: 'Export To CSV' } });
    }
  }, [lastQuery, getCompletionTypes]);

  return (
    <QueryFilters className="TripFilters justify-content-between">
      <div className="d-flex flex-column gap-2">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateRangePicker
              name="dates"
              isDirty={state.from !== lastQuery.from || state.to !== lastQuery.to}
              value={[state.from, state.to] as any}
              onChange={(event) => {
                const [from, to] = event.target.value?.split?.(' - ') || [null, null];
                handleChange('from')(from);
                handleChange('to')(to);
              }}
            />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <AirportGroupDropdown
              name="airportCode"
              value={state.airportCode}
              onChange={(value: string[]) => {
                handleChange('airportCode')(value);
                return false;
              }}
              onEnter={handleSubmit}
              options={{ locale: { 'Select...': 'Airport' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <TripsTableFormatDropdown
              name="format"
              placeholder={displayFormatPlaceholder(state.format)}
              value={state.format || initAirlineTripsFiltersState?.format}
              onChange={(val: string): void => {
                if (!val) return;
                handleChange('format')(val);
              }}
              onEnter={handleSubmit}
              options={{
                showClearButton: false,
              }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <Input
              name="flightNumber"
              placeholder="Flight"
              value={state.flightNumber || ''}
              onChange={handleChange('flightNumber')}
              onKeyDown={onEnter((e: React.KeyboardEvent<HTMLInputElement>) => {
                handleChange('flightNumber')((e.target as HTMLInputElement).value);
                handleSubmit();
              })}
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <FormButton variant="outline-secondary-subtle" onClick={onExpand}>
              {!expanded ? 'More' : 'Less'}
            </FormButton>
            <FormButton name="submitFilters" variant="success" className="border-white" disabled={loading} onClick={handleSubmit}>
              {(loading && <i className="fa fa-spinner fa-pulse" />) || 'Go'}
            </FormButton>
            <FormButton name="resetFilters" variant="outline-secondary-subtle" onClick={handleReset}>
              Reset
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <InputWithIcon
              icon={<i className="sv sv-magnifier fs-4" />}
              name="search"
              value={state.search || ''}
              onChange={handleChange('search')}
              onBlur={handleSearch}
              onKeyDown={onEnter(handleSearch)}
              placeholder="Search"
            />
          </QueryFilters.Control>
        </div>
        <Collapse in={expanded}>
          <div>
            <div className="d-flex gap-2">
              <QueryFilters.Input>
                <ClientDropdown
                  name="payerProviderId"
                  value={state.payerProviderId || initAirlineTripsFiltersState?.payerProviderId}
                  onChange={handleChange('payerProviderId')}
                  onEnter={handleSubmit}
                  options={{ locale: { 'Select...': 'Clients' } }}
                />
              </QueryFilters.Input>
              <HasPermission name="allowSearchTripsByRate">
                <QueryFilters.Input>
                  <RateValuesDropdown
                    name="rateAmount"
                    placeholder="Rate"
                    value={state.rateAmount}
                    onEnter={handleSubmit}
                    onChange={handleChange('rateAmount')}
                  />
                </QueryFilters.Input>
              </HasPermission>
              <QueryFilters.Input>
                <AirlineIataDropdown
                  onEnter={handleSubmit}
                  name="servicerIataAirlineCode"
                  value={state.servicerIataAirlineCode || initAirlineTripsFiltersState?.servicerIataAirlineCode}
                  onChange={handleChange('servicerIataAirlineCode')}
                />
              </QueryFilters.Input>
              <QueryFilters.Input>
                <TripTypeDropdownMulti name="type" value={state.type} onEnter={handleSubmit} onChange={handleChange('type')} />
              </QueryFilters.Input>
              <QueryFilters.Input>
                <LocationQueryDropdown
                  name="puLocationId"
                  value={state.puLocationId}
                  onChange={handleChange('puLocationId')}
                  onEnter={handleSubmit}
                  airports={state.airportCode?.length ? state?.airportCode : null}
                  options={{
                    showAirportPrefix: false,
                    locale: { 'Select...': 'Pickups' },
                  }}
                />
              </QueryFilters.Input>
              <QueryFilters.Input>
                <LocationQueryDropdown
                  name="doLocationId"
                  onEnter={handleSubmit}
                  value={state.doLocationId}
                  onChange={handleChange('doLocationId')}
                  airports={state.airportCode?.length ? state.airportCode : null}
                  options={{
                    showAirportPrefix: false,
                    locale: { 'Select...': 'Dropoffs' },
                  }}
                />
              </QueryFilters.Input>
            </div>
          </div>
        </Collapse>
      </div>
    </QueryFilters>
  );
};

const formatReportInput = (
  {
    from,
    to,
    servicerIataAirlineCode,
    airportCode,
    payerProviderId,
    doLocationId,
    puLocationId,
    rateAmount,
    type,
    format,
  }: AirlineTripsFiltersState,
  completionTypes: string[]
): ReportInput => {
  const payload: ReportInput = { series: ReportSeriesEnum.SeriesDefault, startDatetime: from, endDatetime: to };
  const complexFilters = convertTripTableFormatEnumToComplexFilters(format);
  // conditionally apply fields from props
  if (servicerIataAirlineCode) payload.airlines = [servicerIataAirlineCode];
  if (airportCode) payload.airports = airportCode;
  if (payerProviderId) payload.clients = [payerProviderId];
  if (completionTypes) payload.completionTypes = completionTypes;
  if (complexFilters) payload.complexFilters = complexFilters;
  if (doLocationId) payload.doLocations = doLocationId;
  if (puLocationId) payload.puLocations = puLocationId;
  if (rateAmount === 'NO_RATE') payload.complexFilters = [...(payload.complexFilters || []), ReportFilterEnum.ZeroRate];
  else if (rateAmount) payload.rates = [rateAmount];
  if (type) payload.types = type;
  return payload;
};

const convertTripTableFormatEnumToComplexFilters = (format: TripTableFormatEnum): ReportFilterEnum[] => {
  switch (format) {
    case TripTableFormatEnum.Completed:
      return [ReportFilterEnum.HasCompletion];
    case TripTableFormatEnum.Unassigned:
      return [ReportFilterEnum.HasNoDriver];
    default:
      return null;
  }
};

const displayFormatPlaceholder = (format: TripTableFormatEnum): string => {
  switch (format) {
    case TripTableFormatEnum.United:
      return 'UA Cancels';
    case TripTableFormatEnum.Priority:
      return 'Priority';
    case TripTableFormatEnum.ZeroRates:
      return 'Zero Rates';
    default:
      return undefined;
  }
};

export default React.memo(React.forwardRef(TripFilters));
